import { useMemo } from 'react';
import type { WordEntry, SearchType } from '@spellion/shared';

/**
 * ResultsPanel Component
 * 
 * Displays search results with animations, loading states, and grouping functionality.
 * 
 * Features:
 * - Display list of words with their length
 * - "Aucun résultat" message when empty
 * - Entry animation (200-400ms with easing)
 * - Minimum 8px vertical spacing between entries
 * - Skeleton loading state
 * - Word grouping by length for anagram results
 * 
 * @validates Requirements 1.3, 1.4, 3.5, 11.1, 11.4
 */

export interface ResultsPanelProps {
  /** Search results to display */
  results: WordEntry[] | null;
  /** Whether a search is in progress */
  isLoading: boolean;
  /** Type of search performed */
  searchType: SearchType;
  /** Callback when a word is clicked */
  onWordClick?: (word: string) => void;
  /** Total count of results */
  totalCount?: number;
  /** Search time in milliseconds */
  searchTimeMs?: number | null;
}

/**
 * Groups words by their length
 * @param words - Array of word entries
 * @returns Map of length -> words
 */
function groupWordsByLength(words: WordEntry[]): Map<number, WordEntry[]> {
  const groups = new Map<number, WordEntry[]>();
  
  for (const entry of words) {
    const existing = groups.get(entry.length) || [];
    existing.push(entry);
    groups.set(entry.length, existing);
  }
  
  // Sort keys in descending order (longest first)
  return new Map([...groups.entries()].sort((a, b) => b[0] - a[0]));
}

/**
 * Skeleton loading component for results
 */
function ResultsSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Chargement des résultats">
      {/* Fake group header */}
      <div className="h-6 w-32 shimmer rounded" />
      
      {/* Fake word cards */}
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="h-10 shimmer rounded-lg"
            style={{ width: `${80 + Math.random() * 60}px` }}
          />
        ))}
      </div>
      
      {/* Second fake group */}
      <div className="h-6 w-28 shimmer rounded mt-4" />
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-10 shimmer rounded-lg"
            style={{ width: `${60 + Math.random() * 50}px` }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Empty state component
 */
function EmptyState() {
  return (
    <div 
      className="flex flex-col items-center justify-center py-12 text-center"
      role="status"
      aria-label="Aucun résultat trouvé"
    >
      <div className="w-16 h-16 mb-4 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
        <span className="text-3xl" aria-hidden="true">🔍</span>
      </div>
      <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
        Aucun résultat
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 max-w-xs">
        Aucun mot n'a été trouvé pour cette recherche. Essayez avec d'autres lettres ou ajoutez un joker (*).
      </p>
    </div>
  );
}

/**
 * Word card component with animation
 */
interface WordCardProps {
  word: string;
  length: number;
  index: number;
  onClick?: (word: string) => void;
}

function WordCard({ word, length, index, onClick }: WordCardProps) {
  // Calculate animation delay based on index (stagger effect)
  // Cap at 300ms to avoid too long delays for large result sets
  // Animation duration is 300ms (within 200-400ms per requirement 11.1)
  const animationDelay = Math.min(index * 30, 300);
  
  return (
    <button
      type="button"
      onClick={() => onClick?.(word)}
      className="
        inline-flex items-center gap-2 px-3 py-2
        bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600
        text-base font-medium text-slate-900 dark:text-white
        shadow-sm hover:shadow-md hover:border-primary-300 dark:hover:border-primary-500 hover:-translate-y-0.5
        transition-all duration-200 ease-smooth-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
        opacity-0 animate-fade-in-up
        min-h-touch
        feedback
      "
      style={{ animationDelay: `${animationDelay}ms` }}
      title={`Vérifier "${word}" dans le dictionnaire`}
      aria-label={`${word}, ${length} lettres. Cliquer pour vérifier`}
    >
      <span className="text-slate-900 dark:text-white">{word}</span>
      <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-600 px-1.5 py-0.5 rounded">
        {length}
      </span>
    </button>
  );
}

/**
 * Group header for word length groups
 */
interface GroupHeaderProps {
  length: number;
  count: number;
  index: number;
}

function GroupHeader({ length, count, index }: GroupHeaderProps) {
  const animationDelay = Math.min(index * 50, 200);
  
  return (
    <h3 
      className="
        text-sm font-semibold text-slate-700 dark:text-slate-300
        flex items-center gap-2
        opacity-0 animate-fade-in-up
      "
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      <span className="flex items-center gap-1.5">
        <span className="w-6 h-6 flex items-center justify-center bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 rounded-full text-xs font-bold">
          {length}
        </span>
        <span>lettre{length > 1 ? 's' : ''}</span>
      </span>
      <span className="text-slate-400 dark:text-slate-500 font-normal">
        ({count} mot{count > 1 ? 's' : ''})
      </span>
    </h3>
  );
}

/**
 * Search stats component
 */
interface SearchStatsProps {
  totalCount: number;
  searchTimeMs: number | null;
}

function SearchStats({ totalCount, searchTimeMs }: SearchStatsProps) {
  return (
    <div 
      className="
        flex flex-wrap items-center gap-x-3 gap-y-1 
        text-sm text-slate-600 dark:text-slate-400
        pb-4 border-b border-slate-100 dark:border-slate-700
        opacity-0 animate-fade-in-up
      "
      role="status"
      aria-live="polite"
    >
      <span className="font-medium text-slate-900 dark:text-white">
        {totalCount} mot{totalCount > 1 ? 's' : ''} trouvé{totalCount > 1 ? 's' : ''}
      </span>
      {searchTimeMs !== null && (
        <span className="text-slate-400 dark:text-slate-500">
          en {searchTimeMs < 1000 ? `${searchTimeMs}ms` : `${(searchTimeMs / 1000).toFixed(2)}s`}
        </span>
      )}
    </div>
  );
}

/**
 * Main ResultsPanel component
 */
export function ResultsPanel({
  results,
  isLoading,
  searchType,
  onWordClick,
  totalCount = 0,
  searchTimeMs = null,
}: ResultsPanelProps) {
  // Group words by length for anagram searches
  const groupedWords = useMemo(() => {
    if (!results || results.length === 0) return null;
    
    // Only group for anagram searches, pattern searches are already sorted alphabetically
    if (searchType === 'anagram') {
      return groupWordsByLength(results);
    }
    
    // For pattern and verify, return as single group
    return new Map([[0, results]]);
  }, [results, searchType]);

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 p-6">
        <ResultsSkeleton />
      </div>
    );
  }

  // No results yet (initial state before search)
  if (results === null) {
    return null;
  }

  // Empty results
  if (results.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 p-6">
        <EmptyState />
      </div>
    );
  }

  // Calculate global index for animation delays
  let globalIndex = 0;

  return (
    <div 
      className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 p-6"
      role="region"
      aria-label="Résultats de recherche"
    >
      {/* Stats header */}
      <SearchStats totalCount={totalCount || results.length} searchTimeMs={searchTimeMs} />
      
      {/* Results grouped by length */}
      <div className="mt-4 space-y-6">
        {groupedWords && Array.from(groupedWords.entries()).map(([length, words], groupIndex) => {
          const startIndex = globalIndex;
          globalIndex += words.length;
          
          return (
            <div key={length} className="space-y-3">
              {/* Group header (only for anagram searches with multiple lengths) */}
              {searchType === 'anagram' && length > 0 && (
                <GroupHeader 
                  length={length} 
                  count={words.length} 
                  index={groupIndex}
                />
              )}
              
              {/* Word cards with minimum 8px vertical spacing (gap-2 = 8px) */}
              <div className="flex flex-wrap gap-2" role="list">
                {words.map((entry, wordIndex) => (
                  <WordCard
                    key={`${entry.word}-${wordIndex}`}
                    word={entry.word}
                    length={entry.length}
                    index={startIndex + wordIndex}
                    onClick={onWordClick}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ResultsPanel;
